import { useCallback, useMemo, useState } from 'react';
import { dataSourceToUrlQuery } from '../../../util/url.utils';
import type { CaseListingBlokData } from './CaseListing.data';
import { useRouter } from 'next/router';
import type { StoryData } from 'storyblok-js-client';
import type { LocationBlokData } from '../location/Location.data';
import type { CaseBlokData } from '../case/Case.data';

export const filterParams = {
  location: 'location',
  category: 'category',
};

export const filterCases = (location: StoryData<LocationBlokData>, string: string): boolean =>
  location.name === string ||
  location.content.title === string ||
  location.content.country === string ||
  location.content.countries?.includes(string!);

export const getLocationFilters = (locations: CaseListingBlokData['locations_filter']) =>
  locations?.flatMap(({ name, content }) => {
    // Keep stable country values separate from translated labels.
    const countries = content.countries?.length ? content.countries : [name];

    return countries.map((country) => ({
      value: country,
      label:
        countries.length > 1
          ? country
          : content.country || (content.countries?.length ? country : content.title) || country,
    }));
  }) || [];

export function useCaseFilter(
  blok: CaseListingBlokData,
  allCases: Array<StoryData<CaseBlokData>>,
): {
  cases: Array<StoryData<CaseBlokData>>;
  activeCategoryFilter: string | undefined;
  onFilterClick: (filter: string, type: string) => void;
  activeLocationFilter: string | undefined;
} {
  const router = useRouter();

  const getFilterFromUrl = useCallback(
    (filters: Array<string> | undefined, query: string | Array<string> | undefined) =>
      filters?.find((filter) => dataSourceToUrlQuery(filter) === query),
    [],
  );

  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string | undefined>(
    getFilterFromUrl(blok?.category_filters, router.query.category) || blok.category_all,
  );
  const [activeLocationFilter, setActiveLocationFilter] = useState<string | undefined>(
    getLocationFilters(blok.locations_filter).find(
      ({ value, label }) =>
        dataSourceToUrlQuery(value) === router.query.location ||
        dataSourceToUrlQuery(label) === router.query.location,
    )?.value || blok.location_all,
  );

  const onFilterClick = useCallback(
    (filter: string, type: string) => {
      let category;
      let location;

      // Force category to 'all', when we are not on a 'global' location filter.
      if (type === filterParams.location && filter !== blok.location_all) {
        category = blok.category_all;
      }

      // Force location to 'global', when we click a category while we are not on the 'global' location filter.
      if (activeLocationFilter !== blok.location_all && type === filterParams.category) {
        location = blok.location_all;
      }

      if (type === filterParams.location) {
        location = filter;
      }

      if (type === filterParams.category) {
        category = filter;
      }
      if (category) {
        setActiveCategoryFilter(category);
      }

      if (location) {
        setActiveLocationFilter(location);
      }
    },
    [blok.location_all, blok.category_all, activeLocationFilter],
  );

  const cases = useMemo(() => {
    let filteredCases: Array<StoryData<CaseBlokData>>;

    // Filter cases based on Location
    if (activeLocationFilter !== blok.location_all) {
      // location_all now represents a selection of highlighted cases and not ALL cases. That selection is contained in blok.cards
      filteredCases = allCases;
    } else {
      filteredCases = blok.cards.map((card) => card.case);
    }

    // Filter cases based on Category
    if (activeCategoryFilter !== blok.category_all) {
      filteredCases = filteredCases?.filter((item) =>
        item.content.category.includes(activeCategoryFilter as string),
      );
    }

    if (activeLocationFilter !== blok.location_all) {
      filteredCases = filteredCases?.filter((item) =>
        (item.content.locations || []).some((location) =>
          filterCases(location, activeLocationFilter!),
        ),
      );
    }

    return filteredCases;
  }, [
    activeCategoryFilter,
    activeLocationFilter,
    allCases,
    blok.cards,
    blok.category_all,
    blok.location_all,
  ]);

  return {
    cases,
    activeCategoryFilter,
    onFilterClick,
    activeLocationFilter,
  };
}
